/**
 * Geo Params
 * Build geo search parameters (radius, coordinates)
 */

import { state } from '../core/state.js';
import { readUserGeoFromCache } from '../decision-engine/user-geo.js';

/**
 * Get geo search parameters for Algolia
 * Handles complex logic for radius/coordinates based on search type
 * @returns {Object} - { aroundLatLng?, aroundRadius? }
 */
export function getGeoSearchParams() {
  const geoParams = {};
  console.log('📍 getGeoSearchParams called:', {
    hasCoordinates: !!(
      state.coordinates &&
      state.coordinates.lat &&
      state.coordinates.lng
    ),
    coordinates: state.coordinates,
    zip: state.zip,
    hasLocationInput: !!(state.zip && state.zip.trim().length > 0),
    isStateSearch: state.isStateSearch,
    isExcludeUSSearch: state.isExcludeUSSearch,
    regionFilter: state.regionFilter,
  });

  // Individual UK country search (England, Scotland, etc.) without coords: no geo sorting, like US state search
  // UK city search (London, Reading, etc.) has sortByDistanceNoRadius=true + coords: allow through for distance sorting
  if (state.isExcludeUSSearch && state.ukCountryFilter && !state.sortByDistanceNoRadius) {
    return geoParams;
  }

  // UK region search: if sortByDistanceNoRadius=true and we have coords, use them (handled below)
  // Otherwise (old behavior without coords), return empty geoParams (no geo sorting)
  if (state.isExcludeUSSearch && state.regionFilter) {
    console.log('🔍 DEBUG getGeoSearchParams: UK region check', {
      sortByDistanceNoRadius: state.sortByDistanceNoRadius,
      regionFilter: state.regionFilter,
      willReturn: !state.sortByDistanceNoRadius,
    });
    if (!state.sortByDistanceNoRadius) {
      console.log('🔍 DEBUG: Returning empty geoParams (no sortByDistanceNoRadius)');
      return geoParams;
    }
    console.log('🔍 DEBUG: sortByDistanceNoRadius=true, continuing to coords check');
  }
  // No coordinates for non-US search → no geo sorting
  if (
    state.isExcludeUSSearch &&
    !(state.coordinates?.lat != null && state.coordinates?.lng != null)
  ) {
    return geoParams;
  }

  // State-only search: no radius (state is elongated, circle would hit other states).
  // Use user coords for sorting/distance only when user's state matches search state.
  if (state.isStateSearch) {
    const userGeo = readUserGeoFromCache();
    const map = window.CampsUtils?.mapStateNameToCode;
    const userCode =
      (userGeo?.state && (map?.(userGeo.state) || userGeo.state)) || '';
    const searchCode =
      state.searchStateCode ||
      (state.searchStateName && map?.(state.searchStateName)) ||
      '';
    const match =
      searchCode && userCode && (map?.(userCode) || userCode) === searchCode;
    if (match && userGeo?.lat != null && userGeo?.lng != null) {
      geoParams.aroundLatLng = `${userGeo.lat},${userGeo.lng}`;
    }
    return geoParams;
  }

  if (state.coordinates && state.coordinates.lat && state.coordinates.lng) {
    geoParams.aroundLatLng = `${state.coordinates.lat},${state.coordinates.lng}`;
    const defaultRadius = state.embeddedRadius || window.CampsUtils?.defaults?.radius || 250;
    const hasLocationInput = state.zip && state.zip.trim().length > 0;
    const isLocationRecognized = state.geocodeResult?.recognized === true;

    // Apply radius logic:
    // - If sortByDistanceNoRadius is true (UK regions: England/Scotland/UK), don't apply radius (just sort by distance)
    // Apply radius logic:
    // - If location is recognized (e.g., "Chicago"), always apply radius (standard behavior)
    // - If location is NOT recognized (e.g., "sacred heart university" with no matching camps),
    //   do NOT apply radius - show all camps sorted by distance (consistent with runDecisionFlow)
    if (state.sortByDistanceNoRadius) {
      console.log(
        '📍 No radius applied - sortByDistanceNoRadius=true (UK region):',
        {
          regionFilter: state.regionFilter,
          zip: state.zip,
        }
      );
    } else if (hasLocationInput) {
      const shouldApplyRadius = isLocationRecognized;

      if (shouldApplyRadius) {
        geoParams.aroundRadius = defaultRadius * 1609; // 250 miles in meters
        const displayLocation =
          state.geocodeResult?.correctedLocationLabel || state.zip;
        console.log(
          '📍 Applying radius:',
          defaultRadius,
          'miles for location:',
          displayLocation,
          'aroundRadius (meters):',
          geoParams.aroundRadius,
          'recognized:',
          isLocationRecognized
        );
      } else {
        console.log(
          '📍 No radius applied - unrecognized location:',
          {
            zip: state.zip,
            recognized: isLocationRecognized,
          }
        );
      }
    } else {
      console.log('📍 No radius applied - no location input:', {
        zip: state.zip,
      });
    }
  } else {
    console.warn('⚠️ No coordinates available for geo search:', {
      coordinates: state.coordinates,
      zip: state.zip,
    });
  }
  return geoParams;
}

/**
 * Compute showDistance flag based on search context
 * @param {boolean} stepUseDistance - Whether step wants to show distance
 * @param {Object} step - Search plan step
 * @returns {boolean}
 */
export function computeShowDistance(stepUseDistance, step) {
  // UK regions (England/Scotland/UK) with sortByDistanceNoRadius: don't show distance
  if (state.sortByDistanceNoRadius) return false;

  const userGeo = readUserGeoFromCache();
  const hasDates = !!(state.beginDates && state.endDates);
  const hasLocation = !!(state.zip || '').trim();
  const isNonUS = userGeo?.isUS === false;
  const shouldHideDistance = isNonUS && (hasDates || !hasLocation);
  if (shouldHideDistance) return false;
  const isStateStep = step?.key === 'state' || step?.key === 'state-dates';
  if (isStateStep) return !!stepUseDistance;
  if (state.isStateSearch) {
    const map = window.CampsUtils?.mapStateNameToCode;
    const userCode =
      (userGeo?.state && (map?.(userGeo.state) || userGeo.state)) || '';
    return !!(
      state.searchStateCode &&
      userCode &&
      (map?.(userCode) || userCode) === state.searchStateCode
    );
  }
  return !!stepUseDistance;
}

console.log('📦 Geo params module loaded');
